import { useRef, useCallback, useEffect } from 'react';

/**
 * How long an order took to enter, and on which layout.
 *
 * The point of recording this is to answer "is the new layout actually faster
 * at the counter?" with numbers rather than impressions. Without it the layout
 * choice gets decided by whoever is most vocal.
 *
 * WHAT IT MEASURES, precisely, because the caveats matter more than the number:
 *
 *  - Wall-clock from the form mounting to a successful submit. That includes
 *    the customer changing their mind, a phone call mid-order, and someone
 *    walking away for coffee. It is NOT time-on-task.
 *  - Sessions longer than an hour are discarded rather than recorded, since
 *    those are almost certainly a tab left open, and a handful of them would
 *    drag a median around badly.
 *  - Abandoned orders are not recorded at all, so this cannot tell you whether
 *    one layout causes more abandonment. That is a real blind spot and the
 *    first thing to add if the medians come out close.
 *
 * Read medians, never means: one three-hour order would swamp an average.
 */

const MAX_PLAUSIBLE_MS = 60 * 60 * 1000;

export const useOrderEntryTiming = (layout) => {
  const startedAt = useRef(null);
  const layoutRef = useRef(layout);

  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  // Marks the beginning of an entry session. Called on mount, and again after a
  // successful submit so a second order on the same screen is timed from zero
  // rather than inheriting the first one's clock.
  const start = useCallback(() => {
    startedAt.current = Date.now();
  }, []);

  useEffect(() => {
    start();
  }, [start]);

  /** Returns the completed measurement, or null when it should not be recorded. */
  const complete = useCallback(() => {
    const began = startedAt.current;
    start(); // re-arm immediately for the next order
    if (!began) return null;

    const durationMs = Date.now() - began;
    if (durationMs <= 0 || durationMs > MAX_PLAUSIBLE_MS) return null;

    return { layout: layoutRef.current, durationMs };
  }, [start]);

  return { start, complete };
};

export default useOrderEntryTiming;
